// src/components/DesktopStackedPanels.jsx
import React from 'react';
import {
  Card,
  CardContent,
  Box,
  Typography,
  CircularProgress,
  alpha,
  useTheme
} from '@mui/material';
import AnalyticsPanel from './AnalyticsPanel';
import CashflowPanel from './CashflowPanel';
import AdcoPanel from './AdcoPanel';
import ComparisonPanel from './ComparisonPanel';
import dayjs from 'dayjs';

const DesktopStackedPanels = ({
  selectedRows,
  selectedTranches,
  adcoEnabled,
  // Analytics props
  analyticsData,
  analyticsLoading,
  panelAnalyticsTranches,
  onAnalyticsTranchesChange,
  // Price calculator props
  priceTrancheTargets,
  onPriceTrancheTargetsChange,
  yieldTrancheTargets,
  onYieldTrancheTargetsChange,
  spreadTrancheTargets,
  onSpreadTrancheTargetsChange,
  ownPctTrancheTargets,
  onOwnPctTrancheTargetsChange,
  equityTranches,
  priceData,
  // Generate action props
  onGenerate,
  generationLoading,
  generateDisabled,
  hasGeneratedData,
  // Cashflow props
  cashflowData,
  cashflowMonthlyData,
  cashflowGrain,
  onCashflowGrainChange,
  cashflowLoading,
  panelCashflowTranche,
  onCashflowTrancheChange,
  // ADCO props
  panelAdcoTranche,
  onAdcoTrancheChange,
  modifiedDate,
  adcoData,
  adcoApplied,
  onRunAdco,
  adcoLoading,
  generationError,
  // Shock comparison props
  shockBps,
  onShockBpsChange,
  // Comparison panel props
  showComparison,
  comparisonData,
  comparisonLoading,
  onCloseComparison
}) => {
  const theme = useTheme();
  const hasAdco = Object.keys(adcoEnabled).length > 0 && Object.values(adcoEnabled).some(v => v);

  return (
    <>
      {/* ADCO Panel - positioned between deals and metrics */}
      {hasAdco && selectedRows.length > 0 && (
        <AdcoPanel
          dealName={selectedRows[0]['Deal Name']}
          assetType={selectedRows[0]['Type'] || ''}
          tranches={selectedTranches}
          selectedTranche={panelAdcoTranche}
          onTrancheChange={onAdcoTrancheChange}
          modifiedDate={dayjs(modifiedDate).format('YYYY-MM-DD')}
          adcoData={adcoData}
          adcoEnabled={adcoEnabled}
          onRunAdco={onRunAdco}
          loading={adcoLoading}
        />
      )}

      {/* Analytics Panel */}
      <Card
        elevation={4}
        sx={{
          borderRadius: 3,
          background: theme.palette.mode === 'dark'
            ? `linear-gradient(135deg, ${theme.palette.background.paper} 0%, ${alpha(theme.palette.background.paper, 0.8)} 100%)`
            : 'linear-gradient(135deg, #ffffff 0%, #f8fafc 100%)',
          border: `1px solid ${theme.palette.divider}`,
          boxShadow: theme.palette.mode === 'dark'
            ? '0 10px 15px -3px rgba(0, 0, 0, 0.3), 0 4px 6px -2px rgba(0, 0, 0, 0.2)'
            : '0 10px 15px -3px rgba(0, 0, 0, 0.1), 0 4px 6px -2px rgba(0, 0, 0, 0.05)',
          overflow: 'hidden'
        }}
      >
        <Box
          sx={{
            px: 3,
            py: 2.5,
            bgcolor: theme.palette.mode === 'dark' ? '#1e293b' : '#1e293b',
            color: 'white',
            borderBottom: theme.palette.mode === 'dark' ? '1px solid #334155' : '1px solid #334155'
          }}
        >
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
            <Box>
              <Typography
                variant="h6"
                sx={{
                  fontWeight: 600,
                  fontSize: '16px',
                  mb: 0.5,
                  letterSpacing: '-0.025em'
                }}
              >
                Analytics
              </Typography>
            </Box>
            {analyticsLoading && (
              <CircularProgress size={16} sx={{ color: 'rgba(255, 255, 255, 0.7)' }} />
            )}
          </Box>
        </Box>
        <CardContent sx={{ p: 3, bgcolor: theme.palette.background.paper }}>
          <AnalyticsPanel
            analyticsData={analyticsData}
            tranches={selectedTranches}
            selectedTranches={panelAnalyticsTranches}
            onTranchesChange={onAnalyticsTranchesChange}
            loading={analyticsLoading}
            dealName={selectedRows.length > 0 ? selectedRows[0]['Deal Name'] : ''}
            priceTrancheTargets={priceTrancheTargets}
            onPriceTrancheTargetsChange={onPriceTrancheTargetsChange}
            yieldTrancheTargets={yieldTrancheTargets}
            onYieldTrancheTargetsChange={onYieldTrancheTargetsChange}
            spreadTrancheTargets={spreadTrancheTargets}
            onSpreadTrancheTargetsChange={onSpreadTrancheTargetsChange}
            ownPctTrancheTargets={ownPctTrancheTargets}
            onOwnPctTrancheTargetsChange={onOwnPctTrancheTargetsChange}
            equityTranches={equityTranches}
            priceData={priceData}
            shockBps={shockBps}
            adcoApplied={adcoApplied}
            onShockBpsChange={onShockBpsChange}
            onGenerate={onGenerate}
            generationLoading={generationLoading}
            generateDisabled={generateDisabled}
            hasGeneratedData={hasGeneratedData}
            generationError={generationError}
          />
        </CardContent>
      </Card>

      {/* Comparison Panel - Only shown when comparison data exists */}
      {showComparison && comparisonData && (
        <Card
          elevation={4}
          sx={{
            borderRadius: 3,
            background: theme.palette.mode === 'dark'
              ? `linear-gradient(135deg, ${theme.palette.background.paper} 0%, ${alpha(theme.palette.background.paper, 0.8)} 100%)`
              : 'linear-gradient(135deg, #ffffff 0%, #f8fafc 100%)',
            border: `1px solid ${alpha(theme.palette.warning.main, 0.3)}`,
            boxShadow: theme.palette.mode === 'dark'
              ? '0 10px 15px -3px rgba(0, 0, 0, 0.3), 0 4px 6px -2px rgba(0, 0, 0, 0.2)'
              : '0 10px 15px -3px rgba(0, 0, 0, 0.1), 0 4px 6px -2px rgba(0, 0, 0, 0.05)',
            overflow: 'hidden'
          }}
        >
          <Box
            sx={{
              px: 3,
              py: 2.5,
              bgcolor: theme.palette.mode === 'dark' ? '#4a2800' : '#f57c00',
              color: 'white',
              borderBottom: '1px solid rgba(255,255,255,0.2)'
            }}
          >
            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
              <Box>
                <Typography
                  variant="h6"
                  sx={{
                    fontWeight: 600,
                    fontSize: '16px',
                    mb: 0.5,
                    letterSpacing: '-0.025em'
                  }}
                >
                  Comparison View
                </Typography>
              </Box>
              {comparisonLoading && (
                <CircularProgress size={16} sx={{ color: 'rgba(255, 255, 255, 0.7)' }} />
              )}
            </Box>
          </Box>
          <CardContent sx={{ p: 3, bgcolor: theme.palette.background.paper }}>
            <ComparisonPanel
              comparisonData={comparisonData}
              loading={comparisonLoading}
              onClose={onCloseComparison}
            />
          </CardContent>
        </Card>
      )}

      {/* Cashflow Panel */}
      <Card
        elevation={4}
        sx={{
          borderRadius: 3,
          background: theme.palette.mode === 'dark'
            ? `linear-gradient(135deg, ${theme.palette.background.paper} 0%, ${alpha(theme.palette.background.paper, 0.8)} 100%)`
            : 'linear-gradient(135deg, #ffffff 0%, #f8fafc 100%)',
          border: `1px solid ${theme.palette.divider}`,
          boxShadow: theme.palette.mode === 'dark'
            ? '0 10px 15px -3px rgba(0, 0, 0, 0.3), 0 4px 6px -2px rgba(0, 0, 0, 0.2)'
            : '0 10px 15px -3px rgba(0, 0, 0, 0.1), 0 4px 6px -2px rgba(0, 0, 0, 0.05)',
          overflow: 'hidden'
        }}
      >
        <Box
          sx={{
            px: 3,
            py: 2.5,
            bgcolor: theme.palette.mode === 'dark' ? '#1e293b' : '#1e293b',
            color: 'white',
            borderBottom: theme.palette.mode === 'dark' ? '1px solid #334155' : '1px solid #334155'
          }}
        >
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
            <Box>
              <Typography
                variant="h6"
                sx={{
                  fontWeight: 600,
                  fontSize: '16px',
                  mb: 0.5,
                  letterSpacing: '-0.025em'
                }}
              >
                Cashflow
              </Typography>
            </Box>
            {cashflowLoading && (
              <CircularProgress size={16} sx={{ color: 'rgba(255, 255, 255, 0.7)' }} />
            )}
          </Box>
        </Box>
        <CardContent sx={{ p: 3, bgcolor: theme.palette.background.paper }}>
          <CashflowPanel
            cashflowData={cashflowData}
            cashflowMonthlyData={cashflowMonthlyData}
            tranches={selectedTranches}
            selectedTranche={panelCashflowTranche}
            onTrancheChange={onCashflowTrancheChange}
            grain={cashflowGrain}
            onGrainChange={onCashflowGrainChange}
            loading={cashflowLoading}
          />
        </CardContent>
      </Card>
    </>
  );
};

export default DesktopStackedPanels;

